import {
  Alert,
  AlertTitle,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Input,
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
  Spinner,
  Text,
  cn,
} from '@flatfile/baseline'
import { Suspense, memo, useState } from 'react'
import { PersonalAccessToken, createPersonalAccessToken } from '../../api/internal/personalAccessTokens'
import { ResourceList } from '../../components/ResourceList/ResourceList'
import { ResourceListContextProvider } from '../../components/ResourceList/ResourceListContext'
import { ListPagination, ResourceRowComponent } from '../../components/ResourceList/types'
import { useDynamicHeight } from '../../hooks/useDynamicHeight'
import useResource from '../../resources/hooks/useResource'
import { ResourceMap } from '../../resources/manifest'
import { formatDate, formatTime } from '../../utils/format'
import { logger } from '../../utils/logging'

const TokensHeader = () => (
  <thead className='border-b border-gray-200'>
    <tr>
      <th className='px-3 py-2 text-left align-middle h-[42px]' style={{ width: '33%' }}>
        <Text variant='subheadline' emphasized className='text-muted'>
          Name
        </Text>
      </th>
      <th className='px-3 py-2 text-left align-middle h-[42px]' style={{ width: '33%' }}>
        <Text variant='subheadline' emphasized className='text-muted'>
          Created
        </Text>
      </th>
      <th className='px-3 py-2 text-left align-middle h-[42px]' style={{ width: '33%' }}>
        <Text variant='subheadline' emphasized className='text-muted'>
          Last Used
        </Text>
      </th>
      <th className='px-3 py-2 text-left align-middle h-[42px]' style={{ width: '1%' }}></th>
    </tr>
  </thead>
)

const DeleteTokenModal = ({ token, onClose }: { token: PersonalAccessToken; onClose: () => void }) => {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleClick = async () => {
    setLoading(true)
    try {
      await ResourceMap.PERSONAL_ACCESS_TOKENS.delete(token.id)
      setLoading(false)
      onClose()
    } catch (err: any) {
      setLoading(false)
      setError('Failed to delete token')
      logger.error('Failed to delete token', err ?? {})
    }
  }

  return (
    <div className='flex flex-col gap-4'>
      <Text>
        You are about to permanently delete this access token. This action cannot be reversed.
        <span className='font-medium'> Are you sure you want to proceed in deleting "{token.name}"?</span>
      </Text>
      <Button variant='destructive' onClick={handleClick} loading={loading}>
        I understand, delete this token
      </Button>
      {error && (
        <Alert variant='destructive'>
          <AlertTitle>{error}</AlertTitle>
        </Alert>
      )}
    </div>
  )
}

const CreateTokenModal = ({ onClose }: { onClose: () => void }) => {
  const [name, setName] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [createdToken, setCreatedToken] = useState<string | null>(null)

  const handleCreate = async () => {
    if (!name.trim()) {
      setError('Token name is required')
      return
    }

    setLoading(true)
    try {
      const response = await createPersonalAccessToken(name)
      setCreatedToken(response.token)
      setLoading(false)
    } catch (err: any) {
      setLoading(false)
      setError('Failed to create token')
      logger.error('Failed to create token', err ?? {})
    }
  }

  return (
    <div className='flex flex-col gap-4'>
      {!createdToken ? (
        <>
          <Text>Enter a name for your new personal access token:</Text>
          <div className='space-y-2'>
            <Text as='label' htmlFor='token-name' variant='callout'>
              Token Name
            </Text>
            <Input
              id='token-name'
              placeholder='My API Token'
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
          </div>
          <Button onClick={handleCreate} loading={loading} disabled={!name.trim()}>
            Create Token
          </Button>
          {error && (
            <Alert variant='destructive'>
              <AlertTitle>{error}</AlertTitle>
            </Alert>
          )}
        </>
      ) : (
        <>
          <Alert>
            <AlertTitle>Important</AlertTitle>
            This is the only time you'll see this token. Please copy it now and store it securely.
          </Alert>
          <Text variant='headline' emphasized>
            Your new personal access token:
          </Text>
          <div className='flex items-center gap-2'>
            <pre className='flex-1 overflow-auto p-3 bg-gray-200 rounded text-xs'>{createdToken}</pre>
            <Button
              onClick={() => {
                navigator.clipboard.writeText(createdToken || '')
              }}
            >
              Copy
            </Button>
          </div>
          <Button onClick={onClose} variant='primary' className='w-full mt-4'>
            Close
          </Button>
        </>
      )}
    </div>
  )
}

export const PersonalAccessTokensPage = () => {
  const [currentPage, setCurrentPage] = useState(1)
  const [pageCount, setPageCount] = useState(0)
  const [resetCache, setResetCache] = useState(0)
  const pagination: ListPagination = { currentPage, setCurrentPage, pageCount, setPageCount }

  const { addToRefsArray, listHeight } = useDynamicHeight()
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const [selectedToken, setSelectedToken] = useState<PersonalAccessToken | null>(null)

  const handleCreateToken = () => {
    setIsCreateModalOpen(true)
  }

  const handleDeleteSuccess = () => {
    setCurrentPage(1)
    setResetCache((prev) => prev + 1)
  }

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1)
    }
  }

  const handleNextPage = () => {
    if (currentPage < pageCount) {
      setCurrentPage(currentPage + 1)
    }
  }

  const TokensRow: ResourceRowComponent = memo(({ resourceId, onRowActionSuccess: _onRowActionSuccess }) => {
    const token = useResource('PERSONAL_ACCESS_TOKENS', resourceId) as PersonalAccessToken

    if (!token) {
      return <></>
    }

    const handleDelete = () => {
      setSelectedToken(token)
      setIsDeleteModalOpen(true)
    }

    return (
      <tr className='hover:bg-gray-50 rounded-md overflow-hidden p-1'>
        <td className='px-3 py-2 text-left align-middle border-b border-gray-200' style={{ width: '33%' }}>
          <Text className='text-sm font-normal'>{token.name}</Text>
        </td>
        <td
          className='px-3 py-2 text-left align-middle border-b border-gray-200'
          style={{ width: '33%', minWidth: '150px' }}
        >
          <div className='flex items-center gap-1.5'>
            <Text className='text-sm font-normal'>
              {formatDate(token.createdAt)} {formatTime(token.createdAt)}
            </Text>
          </div>
        </td>
        <td
          className='px-3 py-2 text-left align-middle border-b border-gray-200'
          style={{ width: '33%', minWidth: '150px' }}
        >
          {token.lastUsedAt ? (
            <div className='flex items-center gap-1.5'>
              <Text className='text-sm font-normal'>
                {formatDate(token.lastUsedAt)} {formatTime(token.lastUsedAt)}
              </Text>
            </div>
          ) : (
            <div className='flex items-center'>
              <Text className='text-sm text-muted'>-</Text>
            </div>
          )}
        </td>
        <td className='px-3 py-2 text-left align-middle border-b border-gray-200' style={{ width: '1%' }}>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant='ghost'
                size='icon-md'
                icon='dot-grid-1x3-vertical'
                iconProps={{ size: 12 }}
                aria-label='More Options'
              />
            </DropdownMenuTrigger>
            <DropdownMenuContent align='end'>
              <DropdownMenuItem className='text-surface-sentiment-destructive' onClick={handleDelete}>
                Delete token
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </td>
      </tr>
    )
  })

  TokensRow.displayName = 'TokensRow'

  return (
    <>
      <ResourceListContextProvider requestParams={{}} resourceType='PERSONAL_ACCESS_TOKENS'>
        <div className='w-full h-full flex flex-col p-3'>
          <div className='p-3 flex justify-between items-center'>
            <Text variant='title2'>Personal Access Tokens</Text>
            <Button size='sm' onClick={handleCreateToken} icon='plus-large' iconPlacement='left'>
              New Personal Access Token
            </Button>
          </div>
          <div>
            <div ref={addToRefsArray}>
              <div className='flex justify-end items-center px-2 w-full'>
                <Pagination className='py-2 ml-auto !justify-end' iconOnly size='sm'>
                  <PaginationContent>
                    <PaginationPrevious onClick={handlePreviousPage} disabled={currentPage <= 1} />
                    {(() => {
                      const maxVisiblePages = 5
                      let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2))
                      // Always show at least one page
                      const effectivePageCount = Math.max(1, pagination.pageCount)
                      const endPage = Math.min(effectivePageCount, startPage + maxVisiblePages - 1)

                      // Adjust start page if we're near the end
                      if (endPage - startPage + 1 < maxVisiblePages) {
                        startPage = Math.max(1, endPage - maxVisiblePages + 1)
                      }

                      // Generate array of page numbers to display
                      const pages = Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i)

                      return pages.map((pageNum) => (
                        <PaginationItem key={pageNum}>
                          <PaginationLink
                            isActive={currentPage === pageNum}
                            onClick={() => setCurrentPage(pageNum)}
                            className={cn(
                              'w-5 h-5 flex items-center justify-center rounded-full p-0',
                              currentPage === pageNum
                                ? 'bg-surface-sentiment-subtle-neutral border-none'
                                : 'hover:bg-gray-100'
                            )}
                          >
                            {pageNum}
                          </PaginationLink>
                        </PaginationItem>
                      ))
                    })()}
                    <PaginationNext
                      onClick={handleNextPage}
                      disabled={currentPage >= pagination.pageCount || pagination.pageCount <= 1}
                    />
                  </PaginationContent>
                </Pagination>
              </div>
            </div>
          </div>
          <div className='mt-2 w-full flex-grow'>
            <ResourceList
              height={listHeight}
              pagination={pagination}
              headerComponent={TokensHeader}
              rowComponent={TokensRow}
              errorMessage='Error loading tokens.'
              emptyMessage='No personal access tokens found.'
              forceMount={resetCache}
              onRowActionSuccess={handleDeleteSuccess}
            />
          </div>
        </div>
      </ResourceListContextProvider>

      {/* Create Token Modal */}
      <Dialog open={isCreateModalOpen} onOpenChange={setIsCreateModalOpen}>
        <DialogContent>
          <DialogTitle>Create Personal Access Token</DialogTitle>
          <Suspense
            fallback={
              <div className='relative'>
                <Spinner className='absolute inset-0 m-auto' />
              </div>
            }
          >
            <CreateTokenModal
              onClose={() => {
                setIsCreateModalOpen(false)
                setResetCache((prev) => prev + 1)
              }}
            />
          </Suspense>
        </DialogContent>
      </Dialog>

      {/* Delete Token Modal */}
      {selectedToken && (
        <Dialog open={isDeleteModalOpen} onOpenChange={setIsDeleteModalOpen}>
          <DialogContent>
            <DialogTitle>Delete Token</DialogTitle>
            <DeleteTokenModal
              token={selectedToken}
              onClose={() => {
                setIsDeleteModalOpen(false)
                handleDeleteSuccess()
              }}
            />
          </DialogContent>
        </Dialog>
      )}
    </>
  )
}
